import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Marquee. The track holds the children twice and translates -50%, so the loop
   is seamless with no measurement and no JS ticking.
   ──────────────────────────────────────────────────────────────────────────── */

export interface MarqueeProps {
  children: ReactNode;
  /** One full loop, in seconds. Higher is slower. */
  speed?: number;
  pauseOnHover?: boolean;
  /**
   * Which edges fade out. Default `'both'`.
   *
   * `'right'` exists because a caller that puts a fixed label (a "LIVE" pill,
   * say) flush against the marquee's left edge gets that label's neighbour faded
   * into the mask, which reads as a clipped first item. The fade has to be a prop
   * rather than an overridable class: `.mask-fade-x` lives in `@layer components`,
   * so a caller can only beat it with arbitrary-value utilities.
   */
  fade?: 'both' | 'right' | 'left' | 'none';
  className?: string;
}

const FADE = {
  both: 'mask-fade-x',
  right: 'mask-fade-r',
  left: '[mask-image:linear-gradient(to_right,transparent,black_6%)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_6%)]',
  none: '',
} as const;

export function Marquee({
  children,
  speed = 48,
  pauseOnHover = true,
  fade = 'both',
  className,
}: MarqueeProps) {
  return (
    <div className={cn('relative w-full overflow-hidden', FADE[fade], className)}>
      <div
        className={cn(
          'flex w-max animate-marquee',
          pauseOnHover && 'animate-marquee-hover-pause',
        )}
        style={{ animationDuration: `${speed}s` }}
      >
        <div className="flex shrink-0 items-center">{children}</div>
        {/* Duplicate track: the -50% translate lands exactly on the seam. */}
        <div className="flex shrink-0 items-center" aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
